import { createSelector } from '@reduxjs/toolkit';
import { monthsListEn } from 'core/common/constants';
import { RootInitialState } from 'core/common/store/rootReducer';
import { getIsTestingEnv } from 'core/common/store/system';
import { calculateAges } from 'core/common/utils/calculateAges';
import { getNameFromEmail } from 'core/common/utils/getNameFromEmail';
import { getRelationshipTypeByStatus } from 'core/flow/entities';
import { ZodiacService } from 'core/zodiac/services';

export const getUserProfile = (state: RootInitialState) => state.userProfile;

export const getGender = (state: RootInitialState) => state.userProfile.gender;

export const getBirthdate = (state: RootInitialState) => state.userProfile.date;

export const getZodiac = createSelector(getBirthdate, (date) => {
  return ZodiacService.getZodiacSignByDate(date);
});

const getChineseZodiac = createSelector(getBirthdate, (date) => {
  return ZodiacService.getChineseZodiacByDate(date);
});

export const getPartnerBirthdate = (state: RootInitialState) => state.userProfile.partnerDate;

export const getPartnerZodiac = createSelector(getPartnerBirthdate, (partnerDate) => {
  if (!partnerDate) return '';

  return ZodiacService.getZodiacSignByDate(partnerDate);
});

export const getRelationshipStatus = (state: RootInitialState) =>
  state.userProfile.relationshipStatus;

export const getRelationshipType = createSelector(getRelationshipStatus, (status) =>
  getRelationshipTypeByStatus(status),
);

export const getTotalUsersGenderZodiac = createSelector(
  [getGender, getZodiac],
  (gender, zodiac) => {
    if (!zodiac || !gender) return '';

    return ZodiacService.getZodiacData(zodiac)[gender];
  },
);

export const getUserAge = createSelector(getBirthdate, (date) => {
  if (!date) {
    return NaN;
  }

  return calculateAges(date);
});

export const getAgeDecade = createSelector(getUserAge, (userAge) => {
  const agesAmount = userAge.toString();
  const firstInteger = agesAmount[0];
  const flooredAges = Number(firstInteger.padEnd(agesAmount.length, '0'));
  return flooredAges;
});

export const getEmail = (state: RootInitialState) => state.userProfile.email;

export const getIsUserTester = (state: RootInitialState) => {
  const emailWithPlusRegex = /\+(.+?)@/;
  const isTestingEnv = getIsTestingEnv(state);
  const userEmail = getEmail(state);

  if (isTestingEnv) return true;

  if (!userEmail) return false;

  return userEmail.includes('@gen.tech') || emailWithPlusRegex.test(userEmail);
};

export const getRelationshipSuccessDay = (state: RootInitialState) =>
  state.userProfile.relationshipSuccessDay;

export const getRelationshipProgress = createSelector(getRelationshipSuccessDay, (successDay) => {
  const currentMonthIdx = new Date().getMonth();
  const currentMonth = monthsListEn[currentMonthIdx];
  const futureMonth = monthsListEn[(currentMonthIdx + 2) % monthsListEn.length];
  const futureDate = `${futureMonth} ${successDay ?? 25}`;

  return { currentMonth, futureMonth, futureDate };
});

export const getUserInformation = createSelector(
  [
    getZodiac,
    getChineseZodiac,
    getPartnerZodiac,
    getTotalUsersGenderZodiac,
    getUserAge,
    getAgeDecade,
    getRelationshipType,
    getUserProfile,
    getRelationshipProgress,
  ],
  (
    zodiac,
    chineseZodiac,
    partnerZodiac,
    totalUsersGenderZodiac,
    age,
    ageDecade,
    relationshipType,
    userProfile,
    relationshipProgress,
  ) => ({
    ...userProfile,
    zodiac,
    chineseZodiac,
    partnerZodiac,
    totalUsersGenderZodiac,
    age,
    ageDecade,
    relationshipType,
    ...relationshipProgress,
  }),
);

export const getUserName = createSelector(
  [getUserInformation, getEmail],
  (userInformation, email) => {
    return userInformation.name || getNameFromEmail(email);
  },
);

export const getAppLink = (state: RootInitialState) => state.userProfile.downloadAppLink;

export const getAppLinkLoadingStatus = (state: RootInitialState) =>
  state.userProfile.downloadAppLinkLoadingStatus;

export const getEmailDomain = createSelector(getEmail, (email) => {
  return email.split('@')[1];
});

export const getUserType = createSelector(getUserProfile, (userProfile) => userProfile.userType);
